import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Card, Accordion, Modal, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUtensils, faInfoCircle, faCheckCircle, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import CartContext from '../context/CartContext';
import { useCustomOrderPersistence } from '../hooks/useCustomOrderPersistence';
import './pages.css';

const CustomOrderPage = () => {
  const navigate = useNavigate();
  const { addCustomOrderToCart } = useContext(CartContext);
  const [formData, setFormData, clearFormData] = useCustomOrderPersistence({
    orderType: 'cake',
    size: '',
    flavor: '',
    frosting: '',
    filling: '',
    decorations: '',
    specialInstructions: '',
    deliveryDate: '',
    quantity: 1,
    customerName: '',
    phoneNumber: '',
    deliveryAddress: ''
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState('0');
  const [validationErrors, setValidationErrors] = useState({});

  const cakeTypes = [
    { value: 'birthday', label: 'Birthday Cake' },
    { value: 'wedding', label: 'Wedding Cake' },
    { value: 'anniversary', label: 'Anniversary Cake' },
    { value: 'custom', label: 'Custom Design' }
  ];

  const cakeSizes = [
    { value: 'small', label: 'Small (6-8 servings)', price: 8000 },
    { value: 'medium', label: 'Medium (10-12 servings)', price: 12000 },
    { value: 'large', label: 'Large (15-20 servings)', price: 18000 },
    { value: 'xlarge', label: 'Extra Large (25+ servings)', price: 25000 }
  ];

  const flavors = [
    'Vanilla', 'Chocolate', 'Red Velvet', 'Strawberry', 
    'Lemon', 'Coconut', 'Carrot', 'Marble', 'Other'
  ];

  const frostings = [
    'Buttercream', 'Cream Cheese', 'Fondant', 'Whipped Cream',
    'Chocolate Ganache', 'Royal Icing', 'Other'
  ];

  const fillings = [
    'None', 'Fruit Preserves', 'Chocolate Mousse', 'Vanilla Custard',
    'Cream Cheese', 'Lemon Curd', 'Other'
  ];

  useEffect(() => {
    // Load saved form data from localStorage
    const savedFormData = localStorage.getItem('customOrderFormData');
    if (savedFormData) {
      setFormData(JSON.parse(savedFormData));
    }
  }, [setFormData]);

  useEffect(() => {
    // Save form data to localStorage whenever it changes
    localStorage.setItem('customOrderFormData', JSON.stringify(formData));
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'size', 'flavor', 'frosting', 'deliveryDate', 
      'customerName', 'phoneNumber'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = 'This field is required';
      }
    });

    // Validate phone number format
    if (formData.phoneNumber && !/^[0-9]{11}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid 11-digit phone number';
    }

    // Validate delivery date is in the future
    if (formData.deliveryDate) {
      const today = new Date();
      const deliveryDate = new Date(formData.deliveryDate);
      if (deliveryDate <= today) {
        errors.deliveryDate = 'Delivery date must be in the future';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculatePrice = () => {
    const selectedSize = cakeSizes.find(size => size.value === formData.size);
    let basePrice = selectedSize ? selectedSize.price : 0;
    
    // Additional charges for premium options
    if (formData.frosting === 'Fondant') basePrice += 3000;
    if (formData.filling && formData.filling !== 'None') basePrice += 2000;
    
    return basePrice * formData.quantity;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const orderDetails = {
        ...formData,
        price: calculatePrice(),
        name: `Custom ${formData.orderType.charAt(0).toUpperCase() + formData.orderType.slice(1)} Cake`,
        image: getPreviewImage(),
        timestamp: new Date().toISOString()
      };
      
      addCustomOrderToCart(orderDetails);
      setShowSuccessModal(true);
      
      clearFormData();
    }
  };

  const getPreviewImage = () => {
    // Return different placeholder images based on cake type
    switch(formData.orderType) {
      case 'birthday':
        return 'https://images.unsplash.com/photo-1558301211-0d8c8ddee6ec';
      case 'wedding':
        return 'https://images.unsplash.com/photo-1519671482749-fd09be7ccebf';
      case 'anniversary':
        return 'https://images.unsplash.com/photo-1571115177098-24ec42ed204d';
      default:
        return 'https://images.unsplash.com/photo-1559847844-5315695dadae';
    }
  };

  return (
    <div className="custom-order-page">
      {/* Hero Section */}
      <section className="custom-order-hero bg-danger text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-3">Custom Orders</h1>
              <p className="lead">
                Create your perfect cake or dessert with our custom order service
              </p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <div className="d-inline-block bg-white text-danger p-3 rounded">
                <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                <strong>48 hours notice required</strong> for custom orders
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <Container className="my-5">
        <Row>
          {/* Order Form */}
          <Col lg={8} className="mb-5 mb-lg-0">
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h2 className="mb-4">
                  <FontAwesomeIcon icon={faUtensils} className="me-2 text-danger" />
                  Custom Order Details
                </h2>
                
                <Form onSubmit={handleSubmit}>
                  {/* Order Type Selection */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">What are you ordering?</Form.Label>
                    <div className="d-flex flex-wrap gap-3 mt-2">
                      {cakeTypes.map((type) => (
                        <Button
                          key={type.value}
                          variant={formData.orderType === type.value ? 'danger' : 'outline-secondary'}
                          onClick={() => setFormData({...formData, orderType: type.value})}
                          type="button"
                          className="text-nowrap"
                        >
                          {type.label}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>

                  <Accordion activeKey={activeAccordion} onSelect={(e) => setActiveAccordion(e)}>
                    {/* Cake Details */}
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <div className="d-flex justify-content-between w-100 pe-3">
                          <span>Cake Specifications</span>
                          <FontAwesomeIcon icon={faChevronDown} className={activeAccordion === '0' ? 'rotate-icon' : ''} />
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="px-0">
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Size <span className="text-danger">*</span></Form.Label>
                              <Form.Select 
                                name="size"
                                value={formData.size}
                                onChange={handleChange}
                                isInvalid={!!validationErrors.size}
                              >
                                <option value="">Select size</option>
                                {cakeSizes.map((size) => (
                                  <option key={size.value} value={size.value}>
                                    {size.label} (₦{size.price.toLocaleString()})
                                  </option>
                                ))}
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.size}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Quantity</Form.Label>
                              <Form.Control
                                type="number"
                                name="quantity"
                                min="1"
                                max="10"
                                value={formData.quantity}
                                onChange={handleChange}
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Flavor <span className="text-danger">*</span></Form.Label>
                          <div className="d-flex flex-wrap gap-2">
                            {flavors.map((flavor) => (
                              <Button
                                key={flavor}
                                variant={formData.flavor === flavor ? 'danger' : 'outline-secondary'}
                                onClick={() => setFormData({...formData, flavor})}
                                type="button"
                                size="sm"
                              >
                                {flavor}
                              </Button>
                            ))}
                          </div>
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {validationErrors.flavor}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Frosting Type <span className="text-danger">*</span></Form.Label>
                          <div className="d-flex flex-wrap gap-2">
                            {frostings.map((frosting) => (
                              <Button
                                key={frosting}
                                variant={formData.frosting === frosting ? 'danger' : 'outline-secondary'}
                                onClick={() => setFormData({...formData, frosting})}
                                type="button"
                                size="sm"
                              >
                                {frosting}
                              </Button>
                            ))}
                          </div>
                          <Form.Control.Feedback type="invalid" className="d-block">
                            {validationErrors.frosting}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Filling</Form.Label>
                          <div className="d-flex flex-wrap gap-2">
                            {fillings.map((filling) => (
                              <Button
                                key={filling}
                                variant={formData.filling === filling ? 'danger' : 'outline-secondary'}
                                onClick={() => setFormData({...formData, filling})}
                                type="button"
                                size="sm"
                              >
                                {filling}
                              </Button>
                            ))}
                          </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Decorations/Special Requests</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="decorations"
                            value={formData.decorations}
                            onChange={handleChange}
                            placeholder="Describe any special decorations, colors, or themes"
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Special Instructions</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="specialInstructions"
                            value={formData.specialInstructions}
                            onChange={handleChange}
                            placeholder="Allergies, dietary restrictions, etc."
                          />
                        </Form.Group>
                      </Accordion.Body>
                    </Accordion.Item>

                    {/* Delivery Details */}
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        <div className="d-flex justify-content-between w-100 pe-3">
                          <span>Delivery Information</span>
                          <FontAwesomeIcon icon={faChevronDown} className={activeAccordion === '1' ? 'rotate-icon' : ''} />
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="px-0">
                        <Form.Group className="mb-3">
                          <Form.Label>Delivery/Pickup Date <span className="text-danger">*</span></Form.Label>
                          <InputGroup>
                            <InputGroup.Text>
                              <FontAwesomeIcon icon={faCalendarAlt} />
                            </InputGroup.Text>
                            <Form.Control
                              type="date"
                              name="deliveryDate"
                              value={formData.deliveryDate}
                              onChange={handleChange}
                              isInvalid={!!validationErrors.deliveryDate}
                            />
                          </InputGroup>
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.deliveryDate}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Please allow at least 48 hours for custom orders
                          </Form.Text>
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="customerName"
                                value={formData.customerName}
                                onChange={handleChange}
                                isInvalid={!!validationErrors.customerName}
                              />
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.customerName}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="tel"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                isInvalid={!!validationErrors.phoneNumber}
                                placeholder="08012345678"
                              />
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.phoneNumber}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3">
                          <Form.Label>Delivery Address</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="deliveryAddress"
                            value={formData.deliveryAddress}
                            onChange={handleChange}
                            placeholder="Required if delivery is selected"
                          />
                        </Form.Group>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>

                  <div className="d-grid mt-4">
                    <Button variant="danger" size="lg" type="submit">
                      Add to Cart - ₦{calculatePrice().toLocaleString()}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Order Summary and Help */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-3">Your Custom Order</h3>
                
                {formData.size ? (
                  <>
                    <div className="mb-3">
                      <h5 className="text-danger">{formData.orderType.charAt(0).toUpperCase() + formData.orderType.slice(1)} Cake</h5>
                      <ul className="list-unstyled">
                        <li><strong>Size:</strong> {cakeSizes.find(s => s.value === formData.size)?.label}</li>
                        <li><strong>Flavor:</strong> {formData.flavor || 'Not selected'}</li>
                        <li><strong>Frosting:</strong> {formData.frosting || 'Not selected'}</li>
                        {formData.filling && <li><strong>Filling:</strong> {formData.filling}</li>}
                        <li><strong>Quantity:</strong> {formData.quantity}</li>
                      </ul>
                    </div>
                    
                    <div className="border-top pt-3">
                      <h5 className="d-flex justify-content-between">
                        <span>Estimated Total:</span>
                        <span>₦{calculatePrice().toLocaleString()}</span>
                      </h5>
                    </div>
                  </>
                ) : (
                  <p className="text-muted">Your order details will appear here</p>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
              <Card.Body className="p-4">
                <h3 className="mb-3">Need Help?</h3>
                <p>
                  Our team is happy to assist with your custom order. For complex designs or special requests:
                </p>
                <ul>
                  <li>Call: <strong>+234 700 NOUREESH</strong></li>
                  <li>WhatsApp: <strong>+234 700 NOUREESH</strong></li>
                  <li>Email: <strong>orders@noureeshfoods.com</strong></li>
                </ul>
                <Button variant="outline-danger" className="w-100" href="/contact">
                  Contact Us
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Body className="text-center p-5">
          <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-3" size="4x" />
          <h3 className="mb-3">Order Added to Cart!</h3>
          <p>Your custom {formData.orderType} order has been added to your cart.</p>
          <div className="d-grid gap-2">
            <Button variant="danger" onClick={() => navigate('/menu')}>
              Continue Shopping
            </Button>
            <Button variant="outline-danger" onClick={() => navigate('/cart')}>
              Proceed to Cart
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CustomOrderPage;